import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import Formatted from '@flipster/react/design-utils/formatted';
import DocumentHistoryIcon from '@flipster/react/icon/generated/DocumentHistoryIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css, cx } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { generateLinkWithQueryParams } from '@flipster/utils/url';

import { sparkLaunchpoolItemBunja } from './bunja';

const SparkTotalReward = () => {
  const router = useRouter();
  const { launchpoolItemAtom, isClosedAtom, rewardCurrencyPrecision } = useBunja(sparkLaunchpoolItemBunja);
  const isClosed = useAtomValue(isClosedAtom);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { id, rewardCurrency, estimatedReward, status, stakedAmount } = launchpool;
  const isShowLaunchpoolHistory = status !== LaunchpoolStatus.Upcoming && Number(stakedAmount) !== 0;
  return (
    <div className={css({ mt: 12, pos: 'relative' })}>
      <div>
        <button
          type='button'
          onClick={() =>
            router.push(generateLinkWithQueryParams('/history/launchpool-reward', { launchpoolId: String(id) }))
          }
          disabled={!isShowLaunchpoolHistory}
          className={center({
            pos: 'absolute',
            top: 0,
            right: 0,
            color: 'content.default.level-3',
            _disabled: { color: 'content.interaction.disabled' },
          })}
        >
          <DocumentHistoryIcon size={24} />
        </button>
        <div
          className={cx(
            css({
              display: 'flex',
              flexDirection: { base: 'column', tablet: 'row' },
              justifyContent: 'center',
              alignItems: { tablet: 'center' },
              gap: { base: 8, tablet: 12 },
              color: 'content.default.level-5',
            }),
            isClosed && css({ color: 'content.default.level-2' }),
          )}
        >
          <div className={hstack({ textStyle: 'Text/M/Strong', h: 24 })}>
            <FormattedMessage id='Reward_hub.r251015.Your_total_reward' />
            <button type='button' onClick={alertYourTotalReward} className={css({ buttonShape: 'inline-icon', ml: 4 })}>
              <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
            </button>
          </div>
          <div>
            <Formatted.Number
              value={estimatedReward}
              suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{rewardCurrency}</span>}
              precision={rewardCurrencyPrecision}
              zeroString='-'
              css={hstack.raw({
                textStyle: 'Title/S',
                justify: { tabletDown: 'flex-end' },
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SparkTotalReward;

function alertYourTotalReward() {
  dialog.open({
    title: {
      label: <FormattedMessage id='Reward_hub.r251015.Your_total_reward' />,
    },
    content: (
      <div className={css({ textStyle: 'Text/M/Regular' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Rewards_are_calculated_proportional_to_your_share_of_the_total_staked_amount' />
        <br />
        <br />
        <FormattedMessage id='Autogen_common.r251126.s_Refreshed_daily_at_00_00_UTC' />
        <br />
        <FormattedMessage id='Autogen_common.r251126.s_There_may_be_a_delay_of_a_few_minutes' />
      </div>
    ),
    buttons: {
      primary: <Dialog.Button hierarchy='primary' label='Dismiss' />,
    },
  });
}
